/**
 * uploadProgress — tiny pub/sub for axios `onUploadProgress` events.
 *
 * The shared `apiClient` (auth.js) attaches the same callback to every
 * outgoing request. That callback emits the current { loaded, total }
 * pair here, and a single global <UploadProgressBar /> component
 * subscribes to render the visual UI.
 *
 * Why a custom bus instead of Context? — apiClient is a plain helper
 * called from anywhere (not always inside a React tree), so we need
 * something that works outside of React's component graph.
 */

const listeners = new Set();
let state = { active: false, loaded: 0, total: 0 };
let resetTimer = null;

// Anything below this size is almost certainly JSON / metadata — don't
// flash the upload UI for it. Photos compressed in PhotoUpload land
// around 60–200 KB after compressImage, so 80 KB is a sensible floor.
const MIN_BYTES = 80 * 1024;

export function subscribe(fn) {
  listeners.add(fn);
  // Fire current state immediately so late mounters get the latest snapshot.
  fn(state);
  return () => listeners.delete(fn);
}

function emit() {
  for (const fn of listeners) {
    try { fn(state); } catch { /* keep emitting to the rest */ }
  }
}

export function reportProgress(loaded, total) {
  if (!total || total < MIN_BYTES) return;
  if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
  state = { active: loaded < total, loaded, total };
  emit();
  if (loaded >= total) {
    // Linger at 100% for half a second so the user gets visual confirmation,
    // then collapse the bar.
    resetTimer = setTimeout(() => {
      state = { active: false, loaded: 0, total: 0 };
      emit();
      resetTimer = null;
    }, 500);
  }
}

export function resetProgress() {
  if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
  state = { active: false, loaded: 0, total: 0 };
  emit();
}
